import type { Plan, Role, User, UserStatus } from "@ml/shared";
import { Avatar, Badge, Callout, CopyField, Field, Input, useToast } from "@ml/ui";
import { Wand2 } from "lucide-react";
import { ApiError } from "../../lib/api";
import { planLabel, ROLE_LABELS, STATUS_LABELS } from "../../lib/format";

export const MIN_PASSWORD = 8;

/** 409 `seat_limit` from the API. */
export function isSeatLimitError(err: unknown): boolean {
  return err instanceof ApiError && /seat/i.test(err.code);
}

export function isEmailTakenError(err: unknown): boolean {
  return err instanceof ApiError && (/email/i.test(err.code) || (err.status === 409 && /email/i.test(err.message)));
}

export const SEAT_LIMIT_MESSAGE = "Every seat you've got is taken. Delete an account you don't need, or ask Magically Linked for more seats.";

/** 14 chars, no look-alikes (0/O, 1/l/I). */
export function generatePassword(length = 14): string {
  const alphabet = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const bytes = new Uint32Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join("");
}

/** Visible password field with a "Generate" button — the reseller has to hand it over anyway. */
export function SetPasswordField({ value, onChange, error }: { value: string; onChange: (v: string) => void; error?: string }) {
  return (
    <Field label="Password" hint={`At least ${MIN_PASSWORD} characters. Share it with them securely.`} error={error}>
      <Input
        type="text"
        autoComplete="new-password"
        spellCheck={false}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        suffix={
          <button type="button" className="gfp-inlinebtn" onClick={() => onChange(generatePassword())}>
            <Wand2 size={14} aria-hidden="true" /> Generate
          </button>
        }
      />
    </Field>
  );
}

export function UserCell({ user, photo }: { user: Pick<User, "name" | "email">; photo?: string }) {
  return (
    <span className="gfp-usercell">
      <Avatar src={photo} name={user.name || user.email} size={32} />
      <span className="gfp-usercell__text">
        <span className="gfp-usercell__name">{user.name || "No name yet"}</span>
        <span className="gfp-usercell__email">{user.email}</span>
      </span>
    </span>
  );
}

/** Account status. A suspension with a reason says so ("non_use": a free account that was never put to use). */
export function StatusBadge({ status, reason }: { status: UserStatus; reason?: string | null }) {
  const nonUse = status === "suspended" && reason === "non_use";
  return (
    <Badge size="sm" dot tone={status === "active" ? "success" : "danger"} title={nonUse ? "Free account that was never put to use: no campaigns, DMs or automations ever ran on it. Closed for non-use." : undefined}>
      {nonUse ? "Suspended · not used" : STATUS_LABELS[status]}
    </Badge>
  );
}

export function RoleBadge({ role }: { role: Role }) {
  return (
    <Badge size="sm" tone={role === "admin" ? "gradient" : role === "reseller" ? "info" : "neutral"}>
      {ROLE_LABELS[role]}
    </Badge>
  );
}

export function PlanBadge({ plan }: { plan: Plan }) {
  return (
    <Badge size="sm" tone={plan === "trial" ? "warn" : "accent"}>
      {planLabel(plan)}
    </Badge>
  );
}

/**
 * One-time link display after creating an account or resetting a password.
 * `attempted` = we asked the API to email it; `emailed` = the email actually went out.
 */
export function InviteLinkPanel({ link, attempted, emailed, email }: { link: string; attempted: boolean; emailed: boolean; email: string }) {
  const toast = useToast();
  return (
    <div className="gf-stack">
      {attempted && emailed ? (
        <Callout tone="success">Emailed to {email}. The link below is the same one, if you'd rather send it yourself.</Callout>
      ) : attempted ? (
        <Callout tone="warn" title="The email didn't go out">
          Email isn't set up yet, so nothing was sent. Share this link with them yourself.
        </Callout>
      ) : null}
      <Field label="Set-password link" hint="You won't see this link again after you close this.">
        <CopyField value={link} onCopy={() => toast.success("Link copied")} />
      </Field>
    </div>
  );
}
